import { Container, Paper, Tab, Tabs } from '@mui/material';
import { useConfigService, useSetConfig, useUserManagement } from 'hooks';
import { ColumnState, UserRow } from 'interfaces/index';
import React, { useCallback, useEffect, useReducer, useState } from 'react';
import { useTableStore, useUserStore } from 'state';
import { filterStatus } from 'utils';
import { ConfirmModal, CustomTable, CustomTabPanel, RightDrawer, TableTitle, tabProps, useNoSearchResultsToast, useToastAlert } from '../../common';
import { useExpanded } from '../../components/layouts/ProtectedLayout';
import { Components, TableNames } from '../../customEnums';
import { pageStyles } from '../../customStyles/common/index';
import { ComponentConfig } from '../../customTypes/index';
import { AccessDenied, AccountReset, AddNewUser, EditUserDrawerContent } from "./components";
import { addNewUserReducer, AddUserAction, EditUserAction, editUserReducer } from './helpers';
import { useUserManagementStore } from './state';
import { getOriRfpGroupLiteral, mapNewUserInfo, mapUserTable, setUserLabel } from './utils/transform';

export const UserManagement: React.FC = () => {
  const { expanded } = useExpanded()
  const [currentSecurityLevel, setCurrentSecurityLevel] = useState("")
  const [currentStatus, setCurrentStatus] = useState("")
  const searchParams = useTableStore((state) => state.searchParams)
  const userConfig = useUserStore(state => state.userConfig)
  const { userGroup, usersPermissions } = userConfig;
  const STATE = process.env.REACT_APP_CONFIG_STATE!

  const {
    loading,
    mappedData,
    runCount,
    updateUserGroup,
    updateUserStatus,
    updateUser,
    createUser,
    resendTempPassword,
    resetPassword,
    listUserStatus,
    fetchUsers
  } = useUserManagement(userGroup, currentStatus, currentSecurityLevel, searchParams)

  // Table Store
  const currentCell = useTableStore(state => state.currentCell)
  const setCurrentCell = useTableStore(state => state.setCurrentCell)
  const setHeaderButtonConfig = useTableStore(state => state.setHeaderButtonConfig)
  const setCustomFilterAnchorEl = useTableStore(state => state.setCustomFilterAnchorEl)
  const setDisplayNoSearchResults = useTableStore(state => state.setDisplayNoSearchResults)
  const simpleSearchRan = useTableStore((state) => state.simpleSearchRan)
  const isPost = useTableStore((state) => state.isPost)
  const isDelete = useTableStore((state) => state.isDelete)
  const configBody = useTableStore((state) => state.configBody)
  const columnState: ColumnState = useTableStore(state => state.columnState)
  const tableConfig = useTableStore(state => state.tableConfig)
  const config = useUserStore(state => state.config);
  const groupConfig = config?.cognito?.userRoles;
  const setPageName = useTableStore(state => state.setPageName)
  const setSearchParams = useTableStore((state) => state.setSearchParams)
  const setSimpleSearchRan = useTableStore((state) => state.setSimpleSearchRan);
  useNoSearchResultsToast();

  // useUserManagementStore
  const isActive = useUserManagementStore((state) => state.isActive)
  const setIsActive = useUserManagementStore((state) => state.setIsActive)
  const selectedRow = useUserManagementStore((state) => state.selectedRow);
  const setSelectedRow = useUserManagementStore((state) => state.setSelectedRow)
  const setSelectedUserStatus = useUserManagementStore(state => state.setSelectedUserStatus)
  const disableAddUser = useUserManagementStore(state => state.disableAddUser)
  const setAddNewUserLoading = useUserManagementStore(state => state.setAddNewUserLoading)
  const setRemovedOriRfp = useUserManagementStore(state => state.setRemovedOriRfp)
  const removedOriRfp = useUserManagementStore(state => state.removedOriRfp)
  const setComponent = useTableStore((state) => state.setComponent)
  const component = useTableStore((state) => state.componentConfig)
  const currentDataIndex = useTableStore(state => state.currentDataIndex)

  const [searched, setSearched] = useState<boolean>(false); //Set true when an advanced form multi parameter search has run
  const [value, setValue] = React.useState<any>([]);
  const [status, setStatus] = React.useState<string>("");
  const [groupName, setGroupName] = useState<string>("")
  const [openAddNewUser, setOpenAddNewUser] = useState<boolean>(false)
  const [openDrawer, setOpenDrawer] = useState(false)
  const [data, setData] = useState<any>();
  const [confirmedUser, setConfirmedUser] = useState(true);
  const pageName = "user-management"
  const [tabValue, setTabValue] = React.useState(0);
  const [openConfirmModal, setOpenConfirmModal] = useState(false)
  const [confirmModalTitle, setConfirmModalTitle] = useState("")
  const [confirmModalContent, setConfirmModalContent] = useState<React.ReactNode>("")
  const [cancelButtonText, setCancelButtonText] = useState("CANCEL")
  const [confirmButtonText, setConfirmButtonText] = useState("CONFIRM")
  const [handleConfirmAction, setHandleConfirmAction] = useState<() => void | Promise<void>>(() => { })
  const { notifySuccess, notifyError } = useToastAlert();

  const { rfpListData } = useConfigService("rfpList.json", configBody, true, isPost, isDelete);
  const secondaryOriRequired = ["me", "demo"];

  const { tableHeaderCells, page, rowsPerPage } = useSetConfig(pageName, { page: 0, rowsPerPage: 25 }, TableNames.USER_MANAGEMENT, {})
  const canViewUserManagement = usersPermissions?.["viewUserManagement"] || false

  useEffect(() => {
    setPageName('user-management');
    console.log('UserManagement mounting - clearing search params');
    setSearchParams([]);
    setSimpleSearchRan(false);
  }, []);

  useEffect(() => {
    let securityFilterValues = columnState.page[pageName]['user-management'].filters[0]?.filterValues
    let statusFilterValues = columnState.page[pageName]['user-management'].filters[1]?.filterValues
    console.log('userManagement: ', { userGroup, securityFilterValues, statusFilterValues })
    setData(mapUserTable(mappedData, securityFilterValues, statusFilterValues, rfpListData))

  }, [mappedData, mappedData?.length, page, rowsPerPage, columnState])

  console.log('mapped data: ', mappedData);

  useEffect(() => {
    console.log('debug stuck search results useEffect: ', { runCount, searchParams, mappedData, simpleSearchRan, searched, setDisplayNoSearchResults: mappedData?.length === 0 && simpleSearchRan || mappedData?.length === 0 && searched })
    console.log('debug stuck search results useEffect: ', { conditional: runCount < 1 && searchParams.length > 0, negativeConditional: !(runCount < 1 && searchParams.length > 0) })
    if (runCount < 1 && searchParams.length > 0) {
      setDisplayNoSearchResults(false)
      return
    }
    if (mappedData?.length === 0 && simpleSearchRan || mappedData?.length === 0 && searched) {
      setDisplayNoSearchResults(true)
    }
  }, [mappedData?.length, simpleSearchRan, searched])

  // TODO: improve this reducer so it doesnt filter by env
  const [formState, dispatchFormValues] = useReducer(addNewUserReducer, {
    firstName: '', oriRfpGroups: [
      {
        primaryOri: { field: "Primary ORI", value: "" },
        rfp: { field: "RFP", value: "", required: true }
      },
      secondaryOriRequired.includes(STATE) && {
        secondaryOri: { field: "Secondary ORI", value: "" },
        rfp: { field: "RFP", value: "" }
      }
    ].filter(Boolean)
  })

  //Add trackable index to oriRfpGroups bc deleting one changes map index of oriRfpGroups. 
  // TODO: improve this reducer so it doesnt filter by env
  const [editUserFormState, editUserDispatch] = useReducer(editUserReducer, {
    firstName: '',
    oriRfpGroups: [
      {
        primaryOri: { field: "Primary ORI", value: "" },
        rfp: { field: "RFP", value: "", required: true }
      },
      STATE !== "ga" && STATE !== "nm" && {
        secondaryOri: { field: "Secondary ORI", value: "" },
        rfp: { field: "RFP", value: "" }
      }
    ].filter(Boolean)
  })

  const closeAddNewUser = () => {
    setOpenAddNewUser(false)
    setAddNewUserLoading(false)
    dispatchFormValues({ type: AddUserAction.RESET })
  }

  const handleCreateUser = useCallback(async () => {
    setAddNewUserLoading(true)
    const { firstName, lastName, userName, email, oriRfpGroups, securityLevel } = formState
    const mappedNewUserData = mapNewUserInfo(formState, rfpListData, groupConfig)
    const createUserResp = await createUser(userName, email, lastName, firstName, getOriRfpGroupLiteral(oriRfpGroups, rfpListData), filterStatus(mappedNewUserData?.status), securityLevel);
    if (createUserResp && createUserResp.code === 200) {
      setData([mappedNewUserData, ...data])
      notifySuccess('New User Created', `${userName} was successfully created.`)
      fetchUsers();
    } else {
      notifyError(`New User Creation Failed`, createUserResp.message ?? `Please try again in a few minutes.`)
    }
    closeAddNewUser()
  }, [formState])

  useEffect(() => {
    const headerButtonConfig = {
      handler: () => setOpenAddNewUser(true),
      headerButtonIcon: null,
      buttonLabel: "",
      disabled: !usersPermissions?.viewAddUser,
      text: "ADD NEW USER",
      displayButton: usersPermissions?.viewAddUser,
    }
    setHeaderButtonConfig(headerButtonConfig)
  })

  // User security Update Submission
  const handleSecurityUpdateConfirm = useCallback(async () => {
    setCurrentCell("")
    setOpenConfirmModal(false);
    const user = (selectedRow as UserRow)?.user
    if (groupName && user) {
      const res = await updateUserGroup(groupName, user)
      if (res?.code === 200) {
        notifySuccess('Security Level Updated', `${currentCell} security level was successfully updated.`)
        setData((prevData: any) => {
          return prevData.map((user: any) => {
            if (user.loginName === currentCell) {
              return { ...user, ...selectedRow }
            }
            return user
          })
        })
      } else {
        notifyError(`Account Update Failed`, res.message ?? `Please try again in a few minutes.`)
      }
    }
  }, [groupName, currentCell])

  // User status Update Submission
  const handleStatusUpdateConfirm = useCallback(async () => {
    setCurrentCell("")
    setOpenConfirmModal(false)
    const user = (selectedRow as UserRow)?.user
    if (currentCell && user) {
      const res = await updateUserStatus(isActive, user)
      if (res.code === 200) {
        notifySuccess(`Account ${status === "inactive" ? "Deactivated" : "Activated"}`, `${currentCell}'s account was successfully ${status === "inactive" ? "deactivated" : "activated"}.`)
        setData((prevData: any) => {
          return prevData.map((user: any) => {
            if (user.loginName === currentCell) {
              return { ...user, ...selectedRow }
            }
            return user
          })
        })
      } else {
        notifyError(`Account Update Failed`, res.message ?? `Please try again in a few minutes.`)
      }
    }
  }, [isActive, currentCell, status])

  const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>, currentDataIndex) => {
    const currentDate = Date().slice(4, 15)
    event.stopPropagation();
    setCustomFilterAnchorEl(null)
    setComponent(
      {
        ...component,
        [Components.CUSTOM_FILTER]: {
          ...component[Components.CUSTOM_FILTER],
          selectedValue: event.target.value
        }
      }
    )
    let selectedValue = event.target.value
    let selectedLabel = setUserLabel(selectedValue, setIsActive);

    setSelectedRow({
      ...selectedRow,
      [currentDataIndex]: selectedLabel,
      "lastModified": currentDate
    })

    setOpenConfirmModal(true)
    setCancelButtonText("CANCEL");
    setConfirmModalTitle(currentDataIndex === "securityLevel" ? "UPDATE SECURITY LEVEL?" : "UPDATE STATUS")
    setConfirmButtonText(currentDataIndex === "securityLevel" ? "UPDATE" : "CONTINUE")
    setConfirmModalContent(
      currentDataIndex === "securityLevel" ?
        <div>
          You have chosen to update <b>{currentCell}</b> to security level <b>{selectedLabel}.</b> Would you like to proceed?
        </div> :
        <div>
          You have chosen to <b>{selectedValue === "inactive" ? "deactivate" : "activate"} {currentCell}</b> user account. Would you like to proceed?
        </div>
    )

    if (currentDataIndex === "securityLevel") setGroupName(selectedValue);

    if (currentDataIndex === "status") {
      setHandleConfirmAction(() => handleStatusUpdateConfirm)
      setStatus(selectedValue)
    }
  };

  const componentConfig: ComponentConfig = {
    "CustomFilter": {
      ...component["CustomFilter"],
      handleRadioChange: handleRadioChange
    }
  }

  useEffect(() => {
    setComponent(componentConfig)
    if (groupName) {
      setHandleConfirmAction(() => handleSecurityUpdateConfirm)
    }
    if (currentDataIndex === "status") {
      setHandleConfirmAction(() => handleStatusUpdateConfirm)
    }
  }, [currentCell, currentDataIndex, groupName, status])

  const closeConfirmModal = () => {
    setOpenConfirmModal(false)
  }

  useEffect(() => {
    if (openAddNewUser) {
      setIsActive(true);
      setStatus("active")
    }
  }, [openAddNewUser])

  const handleEditUser = useCallback(async (e, row: UserRow) => {
    setOpenDrawer(true)
    editUserDispatch({ type: EditUserAction.SET_CURRENT, value: row })
    setCurrentStatus(row.status!)
    setCurrentSecurityLevel(row.securityLevel!)
    setCurrentCell(row.loginName);
    setSelectedUserStatus(row.loginStatus!);
    if (row?.loginName) {
      const userStatus = await listUserStatus(row.loginName);
      setConfirmedUser(userStatus?.isConfirmed ?? true)
    }
  }, [])

  const handleUpdateUser = useCallback(async (e) => {
    setRemovedOriRfp([])
    const updateUserResp = await updateUser(editUserFormState)
    setOpenDrawer(false)
    const updateRfp = getOriRfpGroupLiteral(editUserFormState.oriRfpGroups, rfpListData)
    const fullName = editUserFormState.firstName + " " + editUserFormState.lastName
    if (updateUserResp.code === 200) {
      notifySuccess('User Updated', `${fullName} was successfully updated.`)
      setData((prevData: any) => {
        let updatedUserRow = {
          fullName: editUserFormState.firstName + " " + editUserFormState.lastName,
          emailAddress: editUserFormState.email,
          securityLevel: editUserFormState.securityLevel,
          status: editUserFormState.status,
          loginName: editUserFormState.userName,
          primaryOri: editUserFormState?.oriRfpGroups[0]?.primaryOri?.value,
          oriRfpGroups: editUserFormState?.oriRfpGroups,
          secondaryOri: editUserFormState?.oriRfpGroups[1]?.secondaryOri?.value,
          reasonFingerprinted: updateRfp?.[0]["rfp"].value,
          user: editUserFormState.user
        }
        return prevData.map((user: any) => {
          if (user.loginName === currentCell) {
            return { ...user, ...updatedUserRow }
          }
          return user
        })
      })

    } else {
      notifyError('Account Update Failed', updateUserResp.message ?? `Please try again in a few minutes.`)
    }
  }, [editUserFormState, currentCell])

  const handleCancel = useCallback(() => {
    console.log('debug manage oriRfpGroups close drawer button pressed: ', { removedOriRfp })
    setOpenDrawer(false)
    editUserDispatch({ type: EditUserAction.REMOVE_EMPTY_ORI_RFP_GROUP })
    if (removedOriRfp.length > 0) {
      editUserDispatch({ type: EditUserAction.RESET_ORI_RFP_GROUP, removedOriRfp })
      setRemovedOriRfp([])
    }
  }, [editUserFormState?.oriRfpGroups, removedOriRfp])

  if (!canViewUserManagement) {
    <AccessDenied />
  }
  return (
    <Paper elevation={0} sx={{ ...pageStyles.pagePaperStyle, minWidth: `calc(100vw - ${expanded ? "256px" : "64px"})` }}>
      <TableTitle name="User Management" />
      <RightDrawer containsTable={false} openDrawer={openDrawer} closeDrawer={handleCancel}>
        <Container sx={{ mb: "0", mt: '24px' }} disableGutters>
          <Tabs sx={{ "..MuiTabs-root": { height: "42px" } }} value={tabValue} onChange={(e, newValue) => { setTabValue(newValue) }} aria-label=" tabs">
            <Tab label="Information" {...tabProps(0)} data-testid="information-tab" />
            <Tab label="Login and Password" {...tabProps(1)} data-testid="login-and-password-tab" />
          </Tabs>
        </Container>
        <Container disableGutters sx={{ flex: 2, gap: '2rem' }}>
          <CustomTabPanel value={tabValue} index={0}>
            <EditUserDrawerContent
              data={data?.find(user => user.loginName === editUserFormState.userName)}
              formState={[editUserFormState, editUserDispatch]}
              handleCancel={() => handleCancel()}
              closeDrawer={() => setOpenDrawer(false)}
              handleUpdate={handleUpdateUser}
              loading={loading}
              rfpListData={rfpListData}
            />
          </CustomTabPanel>
          <CustomTabPanel value={tabValue} index={1}>
            <AccountReset setOpenDrawer={setOpenDrawer} resendTempPassword={resendTempPassword} resetPassword={resetPassword} confirmedUser={confirmedUser} />
          </CustomTabPanel>
        </Container>
      </RightDrawer>
      {
        tableHeaderCells && (
          <CustomTable
            //Required props
            tableConfig={tableConfig}
            tableName="user-management"
            loading={loading}
            rows={data?.length ? data : []}
            columnState={columnState}
            totalRecords={data?.length} //using data length to keep in sync with filtering, this is possible since we get all records back currently from user table, will need updating if/when user management queries implement query service on BE
            currentPage={page}
            pageSize={rowsPerPage}
            searched={searched}
            value={value}
            setValue={setValue}
            setSearched={setSearched}
            rowClickHandler={handleEditUser} />)
      }
      <ConfirmModal
        title={confirmModalTitle}
        content={confirmModalContent}
        cancelButtonText={cancelButtonText}
        confirmButtonText={confirmButtonText}
        onCancel={closeConfirmModal}
        onConfirm={handleConfirmAction}
        open={openConfirmModal}
      />

      <ConfirmModal
        title="ADD NEW USER"
        content={"Edit the information below and select 'Add User'. Select 'Cancel' to exit. New users will receive a temporary password, and are required to set up both a new password and multi-factor authentication during their first login."}
        cancelButtonText="CANCEL"
        confirmButtonText="ADD USER"
        onCancel={closeAddNewUser}
        onConfirm={handleCreateUser}
        confirmDisabled={disableAddUser}
        loading={loading}
        size="large"
        open={openAddNewUser}
      >
        {openAddNewUser && rfpListData && (
          <AddNewUser
            formState={[formState, dispatchFormValues]}
            rfpListData={rfpListData}
          />
        )}
      </ConfirmModal>
    </Paper>
  )
}